import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshakeSimple, faArrowRight, faGraduationCap, faBriefcase, faCode } from '@fortawesome/free-solid-svg-icons';
//import Footer from '../Footer';
import './About.css';

/* ─── Animation variants ─────────────────────────────────────── */
var fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

var stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.12 } },
};

var fadeLeft = {
  hidden: { opacity: 0, x: -32 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

var fadeRight = {
  hidden: { opacity: 0, x: 32 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

/* ─── Data ───────────────────────────────────────────────────── */
var SKILLS = [
  'React.js','NextJs','HTML & CSS','Bootstrap','Tailwind CSS','JavaScript',
  'Node.js','NestJs','PHP & Laravel','Python',
  'MySQL', 'Git & GitHub',
];

var TIMELINE = [
  {
    icon: faBriefcase,
    year: '2023 – Present',
    role: 'Full-Stack Developer',
    place: 'Freelance & Contract',
    desc: 'Building scalable web applications for clients across multiple industries using React, Node.js, and PHP.',
  },
  {
    icon: faGraduationCap,
    year: '2022 – Present',
    role: 'Computer Science Lecturer',
    place: 'Lincoln University College, Malaysia',
    desc: 'Mentoring the next generation of tech professionals in web development, algorithms, and software engineering.',
  },
  {
    icon: faCode,
    year: '2020 – 2022',
    role: 'Junior Developer',
    place: 'Various Projects',
    desc: 'Developed front-end and back-end solutions, honing skills across the full web stack.',
  },
];

/* ─── Component ──────────────────────────────────────────────── */
var About = function() {
  return (
    <motion.div
      className="about-root"
      initial="hidden"
      animate="show"
      variants={stagger}
    >

      {/* ── Page header ──────────────────────────────────────── */}
      <section className="about-hero" aria-label="About header">
        <div className="about-hero__glow" aria-hidden="true" />
        <Container>
          <motion.span className="home-label" variants={fadeUp}>About Me</motion.span>
          <motion.h1 className="about-hero__title" variants={fadeUp}>
            The person behind<br /><em>the code.</em>
          </motion.h1>
          <motion.p className="about-hero__sub" variants={fadeUp}>
            Full-Stack Developer · Computer Science Lecturer · Creative Technologist
          </motion.p>
        </Container>
      </section>

      {/* ── Bio section ──────────────────────────────────────── */}
      <section className="about-section" aria-labelledby="bio-heading">
        <Container>
          <Row className="align-items-center about-bio-row">

            {/* Photo */}
            <Col lg={4} className="about-photo-col">
              <motion.div className="about-photo-wrap" variants={fadeLeft}>
                <img
                  src="jacob.jpeg"
                  alt="Jacob Chidi Eugene — Full-Stack Developer"
                  className="about-photo"
                  loading="lazy"
                />
                <div className="about-photo__badge">
                  <FontAwesomeIcon icon={faHandshakeSimple} />
                  <span>Open to Work</span>
                </div>
              </motion.div>
            </Col>

            {/* Text */}
            <Col lg={8}>
              <motion.div variants={fadeRight}>
                <span className="home-label">Introduction</span>
                <h2 id="bio-heading" className="about-bio__title">
                  Hi, I'm Jacob Chidi Eugene
                </h2>
                <div className="about-bio__body">
                  <p>
                    I'm a passionate <strong>Snr Software Engineer</strong> with expertise in both front-end, Mobile-app and
                    back-end technologies. My focus is on crafting innovative, high-quality web
                    applications that prioritise user experience while solving real-world problems.
                  </p>
                  <p>
                    I'm proficient across a wide range of languages and frameworks — from React and
                    Node.js on the front end, to PHP (Laravel) and Python on the back end. Beyond
                    building products, I served as a  former <strong>Computer Science Lecturer at Lincoln
                    University NSUK</strong>, where I mentor the next generation of
                    tech professionals.
                  </p>
                  <p>
                    I'm always eager to explore new opportunities, take on challenges, and
                    collaborate on ideas that matter. Let's build something great together.
                  </p>
                </div>
                <div className="about-bio__actions">
                  <Link to="/contact" className="home-btn home-btn--primary">
                    Hire Me <FontAwesomeIcon icon={faHandshakeSimple} />
                  </Link>
                  <Link to="/projects" className="home-btn home-btn--outline">
                    See My Work <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── Skills ───────────────────────────────────────────── */}
      <section className="about-section about-section--alt" aria-labelledby="skills-heading">
        <Container>
          <motion.div className="home-section__header" variants={fadeUp}>
            <span className="home-label">Tech Stack</span>
            <h2 id="skills-heading" className="home-section__title">Skills &amp; Tools</h2>
            <p className="home-section__sub">
              The technologies I use to bring ideas to life.
            </p>
          </motion.div>

          <motion.div className="about-skills-grid" variants={stagger}>
            {SKILLS.map(function(skill) {
              return (
                <motion.span key={skill} className="about-skill-pill" variants={fadeUp}>
                  {skill}
                </motion.span>
              );
            })}
          </motion.div>
        </Container>
      </section>

      {/* ── Timeline ─────────────────────────────────────────── */}
      <section className="about-section" aria-labelledby="timeline-heading">
        <Container>
          <motion.div className="home-section__header" variants={fadeUp}>
            <span className="home-label">Experience</span>
            <h2 id="timeline-heading" className="home-section__title">My Journey</h2>
          </motion.div>

          <motion.div className="about-timeline" variants={stagger}>
            {TIMELINE.map(function(item, i) {
              return (
                <motion.div key={i} className="about-timeline__item" variants={fadeUp}>
                  <div className="about-timeline__icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <div className="about-timeline__body">
                    <span className="about-timeline__year">{item.year}</span>
                    <h3 className="about-timeline__role">{item.role}</h3>
                    <span className="about-timeline__place">{item.place}</span>
                    <p className="about-timeline__desc">{item.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </Container>
      </section>
    </motion.div>
  );
};

export default About;